/**
 * SaveAsWatchlistModal — name and save the current watchlist as a new user watchlist.
 *
 * Validates that the name does not duplicate an existing default or user watchlist.
 * On success calls `saveWatchlist()` which persists the symbols to the server.
 */
import Modal, { Styles } from "react-modal";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { HiOutlineSave } from "react-icons/hi";

import { saveWatchlist } from "@service/watchlist-handler";
import { getColorTheme } from "@service/local-storage-handler";

import "../watchlist-modal-styles.css";

Modal.setAppElement("#marketview");

const SaveAsWatchlistModal = ({
  saveAsUserWatchlistIsOpen,
  setSaveAsUserWatchlistIsOpen,
}) => {
  const {
    marketWatchState: { defaultWatchlist, userWatchlist },
    feedsScripsState: { optionsScrips },
  } = useSelector(({ metadata }) => metadata);

  const symbolList = useSelector(({ watchlist }) => watchlist.scrips);

  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const theme = themeFromStore || getColorTheme() || "dark";

  const [watchlistName, setWatchlistName] = useState("");

  const closeModal = () => {
    setWatchlistName("");
    setSaveAsUserWatchlistIsOpen(false);
  };

  const handleWatchlistNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setWatchlistName(event.target.value.toUpperCase());
  };

  const onSaveAsUserWatchlist = (event) => {
    event.preventDefault();

    const watchlistNameExists =
      defaultWatchlist.some((list) => list.name === watchlistName) ||
      userWatchlist.some((list) => list.name === watchlistName);

    if (watchlistNameExists) {
      // toast.error("Watchlist Name Already E`xists.", { duration: 6000 });
      toast(
        (t) => (
          <span>
            {/* {msg} */}
            Watchlist Name Already Exists &nbsp;&nbsp;
            <button onClick={() => toast.dismiss(t.id)} className="dismiss-btn">
              Dismiss
            </button>
          </span>
        ),
        { duration: 6000 }
      );
      return;
    }

    const scripIds = symbolList.map((scrip) => scrip.id);

    saveWatchlist(watchlistName, scripIds)
      .then(() => {
        toast.success(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  Watchlist saved successfully
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .catch((error) => {
        toast.error(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  {`Error while saving watchlist. ${error}`}
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .finally(() => {
        closeModal();
      });
  };

  return (
    <>
      <Modal
        isOpen={saveAsUserWatchlistIsOpen}
        onRequestClose={() => setSaveAsUserWatchlistIsOpen(false)}
        style={getModalStyles(theme)}
      >
        <div className={`wl-modal ${theme}`}>
          {/* Coloured header banner */}
          <div className="wl-modal__header wl-modal__header--save">
            <div className="wl-modal__header-bg wl-modal__header-bg--save" />
            <div className="wl-modal__icon-wrap">
              <div className="wl-modal__icon">
                <HiOutlineSave />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="wl-modal__body">
            <h3 className="wl-modal__title">Save Watchlist</h3>
            <p className="wl-modal__desc">Create a new watchlist with your current symbols</p>
            <div className="wl-modal__divider" />
            <form onSubmit={onSaveAsUserWatchlist}>
              <div className="wl-modal__field">
                <label className="wl-modal__label" htmlFor="watchlistName">
                  Watchlist Name
                </label>
                <input
                  type="text"
                  id="watchlistName"
                  className="wl-modal__input"
                  placeholder="Enter watchlist name"
                  value={watchlistName}
                  onChange={handleWatchlistNameChange}
                  required
                  autoFocus
                />
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="wl-modal__footer">
            <button type="button" className="wl-modal__btn wl-modal__btn--cancel" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="wl-modal__btn wl-modal__btn--confirm" onClick={onSaveAsUserWatchlist}>
              Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SaveAsWatchlistModal;

const getModalStyles = (theme: string): Styles => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme === "dark" ? "rgba(0,0,0,0.75)" : "rgba(15,23,42,0.55)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },
  content: {
    position: "relative",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    border: "none",
    background: "transparent",
    overflow: "visible",
    WebkitOverflowScrolling: "touch",
    borderRadius: "0",
    outline: "none",
    padding: "0",
    maxWidth: "420px",
    width: "90%",
  },
});
